import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Loader2, MessageCircle, Copy, Check, RefreshCw, Trash2, KeyRound, Clock, AlertTriangle, CheckCircle2 } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
}

function ageMinutes(iso) {
  if (!iso) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
}

function formatAge(mins) {
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins} min`;
  const h = Math.floor(mins / 60);
  return h < 24 ? `il y a ${h}h` : `il y a ${Math.floor(h / 24)}j`;
}

export default function PasswordResetsPanel() {
  const [resets, setResets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/password-resets");
      setResets(data || []);
    } catch (e) {
      toast.error("Erreur chargement", { description: e.response?.data?.detail });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyCode = async (code, id) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch { /* noop */ }
  };

  const sendViaWhatsApp = (r) => {
    if (!r.user_phone) {
      toast.error("Numéro de téléphone manquant pour ce compte");
      return;
    }
    const phone = r.user_phone.replace(/[^\d]/g, "");
    const msg = `🔐 *TrigoTour - Code de réinitialisation*\n\nBonjour ${r.user_name || ""},\n\nVoici votre code pour réinitialiser votre mot de passe:\n\n*${r.code}*\n\n⏱️ Ce code expire dans 30 minutes.\n\nUtilisez-le sur la page de réinitialisation:\nhttps://tour.trigoride.com/forgot-password`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const markDelivered = async (id) => {
    setActionLoading(id);
    try {
      await api.post(`/admin/password-resets/${id}/mark-delivered`);
      toast.success("Marqué comme envoyé");
      load();
    } catch (e) {
      toast.error("Erreur", { description: e.response?.data?.detail });
    } finally {
      setActionLoading(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    setActionLoading(id);
    try {
      await api.delete(`/admin/password-resets/${id}`);
      toast.success("Supprimée");
      load();
    } catch (e) {
      toast.error("Erreur", { description: e.response?.data?.detail });
    } finally {
      setActionLoading(null);
    }
  };

  const pendingCount = resets.filter((r) => !r.is_expired && !r.delivered).length;

  return (
    <div className="space-y-4" data-testid="password-resets-panel">
      <div className="bg-gradient-to-br from-[#0A2540] to-[#1E3A5F] rounded-2xl p-5 text-white">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="w-5 h-5 text-[#FACC15]" />
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit' }}>Réinitialisations de mot de passe</h2>
        </div>
        <p className="text-sm text-white/70">Envoyez les codes par WhatsApp aux utilisateurs qui en font la demande. Les codes expirent après 30 minutes.</p>
        {pendingCount > 0 && (
          <p className="text-xs text-[#FACC15] mt-2 font-semibold">⚡ {pendingCount} demande{pendingCount > 1 ? "s" : ""} en attente d'envoi</p>
        )}
      </div>

      <div className="flex items-center justify-end">
        <button onClick={load} className="h-8 px-3 rounded-full flex items-center gap-1.5 hover:bg-[#F8FAFC] border border-[#CBD5E1] text-xs text-[#64748B]" data-testid="refresh-resets">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-[#CBD5E1] overflow-hidden">
        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#0A2540]" /></div>
        ) : resets.length === 0 ? (
          <div className="py-12 text-center text-[#64748B]">
            <KeyRound className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Aucune demande de réinitialisation</p>
          </div>
        ) : (
          <ul className="divide-y divide-[#E2E8F0]">
            {resets.map((r) => {
              const age = ageMinutes(r.created_at);
              const isProcessing = actionLoading === r.id;
              return (
                <li key={r.id} className={`p-4 ${r.is_expired ? "bg-[#FEE2E2]/30" : r.delivered ? "bg-[#D1FAE5]/20" : ""}`} data-testid={`reset-request-${r.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="text-sm font-bold text-[#0A2540]">{r.user_name || "Utilisateur"}</p>
                        {r.is_expired ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-[#EF4444] bg-[#FEE2E2]">
                            <AlertTriangle className="w-3 h-3" /> Expiré
                          </span>
                        ) : r.delivered ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-[#10B981] bg-[#D1FAE5]">
                            <CheckCircle2 className="w-3 h-3" /> Envoyé
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-[#F59E0B] bg-[#FEF3C7]">
                            <Clock className="w-3 h-3" /> En attente
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-xs text-[#64748B]">
                        <p>📧 {r.email}</p>
                        <p>📞 {r.user_phone || <span className="text-[#EF4444]">Pas de téléphone</span>}</p>
                        <p>🕐 {formatDate(r.created_at)} ({formatAge(age)})</p>
                      </div>
                    </div>
                    <div className="text-center shrink-0">
                      <button onClick={() => copyCode(r.code, r.id)} className="font-mono text-xl font-extrabold text-[#0A2540] bg-[#FACC15]/15 px-3 py-1.5 rounded-lg hover:bg-[#FACC15]/30 flex items-center gap-1.5" data-testid={`reset-code-${r.id}`} title="Copier le code">
                        {r.code}
                        {copiedId === r.id ? <Check className="w-3.5 h-3.5 text-[#10B981]" /> : <Copy className="w-3.5 h-3.5 text-[#64748B]" />}
                      </button>
                      <p className="text-[9px] text-[#64748B] mt-1">Code OTP</p>
                    </div>
                  </div>

                  {!r.is_expired && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-[#E2E8F0]">
                      <Button
                        onClick={() => sendViaWhatsApp(r)}
                        disabled={!r.user_phone || isProcessing}
                        size="sm"
                        className="flex-1 bg-[#25D366] text-white hover:bg-[#1FAE53] h-9"
                        data-testid={`send-whatsapp-${r.id}`}
                      >
                        <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> Envoyer via WhatsApp
                      </Button>
                      {!r.delivered && (
                        <Button
                          onClick={() => markDelivered(r.id)}
                          disabled={isProcessing}
                          size="sm"
                          variant="outline"
                          className="border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10 h-9"
                          data-testid={`mark-delivered-${r.id}`}
                        >
                          {isProcessing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                        </Button>
                      )}
                      <Button
                        onClick={() => remove(r.id)}
                        disabled={isProcessing}
                        size="sm"
                        variant="outline"
                        className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10 h-9"
                        data-testid={`delete-reset-${r.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
